import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { ArrowLeft, ExternalLink, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import Reactions from '../components/Reactions';
import Comments from '../components/Comments';
import ShareCard from '../components/ShareCard';
import AdSlot from '../components/AdSlot';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SITE_URL = (process.env.REACT_APP_SITE_URL || 'https://dailywit.news').replace(/\/$/, '');

const ArticlePage = () => {
  const { slug, newsId } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArticle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, newsId]);

  const fetchArticle = async () => {
    setLoading(true);
    try {
      const endpoint = slug
        ? `${API}/news/slug/${encodeURIComponent(slug)}`
        : `${API}/news/${newsId}`;
      const response = await axios.get(endpoint);
      setArticle(response.data);
      // Redirect legacy /article/:id links to the clean slug URL for SEO
      if (newsId && response.data?.slug) {
        navigate(`/news/${response.data.slug}`, { replace: true });
      }
      // Log the view (powers gamification + trending)
      if (response.data?.id) {
        axios.post(`${API}/view/${response.data.id}`).catch(() => {});
      }
    } catch (error) {
      console.error('Error fetching article:', error);
    } finally {
      setLoading(false);
    }
  };

  const canonicalUrl = article?.slug
    ? `${SITE_URL}/news/${article.slug}`
    : `${SITE_URL}/news/${newsId || ''}`;

  const handleShare = async () => {
    const shareText = article?.humorous_summary
      ? `"${article.humorous_summary}" - Daily Wit`
      : 'Check this out on Daily Wit!';

    if (navigator.share) {
      try {
        await navigator.share({ title: 'Daily Wit', text: shareText, url: canonicalUrl });
      } catch (err) {
        /* share cancelled */
      }
    } else {
      navigator.clipboard.writeText(canonicalUrl);
      toast.success('Link copied to clipboard!');
    }
  };

  const categoryColors = {
    world: '#4ECDC4', politics: '#FF6B6B', economy: '#FFE66D',
    tech: '#9B59B6', science: '#3498DB', sports: '#2ECC71', celebrity: '#E91E63',
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFDF5] dark:bg-[#1a1816] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#FF6B6B] border-t-transparent"></div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-[#FFFDF5] dark:bg-[#1a1816] flex flex-col items-center justify-center">
        <Helmet>
          <title>Article Not Found - Daily Wit</title>
        </Helmet>
        <h1 className="text-2xl font-bold dark:text-white mb-4">Article not found</h1>
        <Link to="/" className="text-[#4ECDC4] hover:underline">← Back to Home</Link>
      </div>
    );
  }

  const shareImage = article.image_url || `${SITE_URL}/og-image.png`;
  const metaTitle = `${article.humorous_summary || article.title} - Daily Wit`;
  const metaDescription = article.humorous_summary
    ? `"${article.humorous_summary}" — ${article.title}`
    : article.title;

  return (
    <div className="min-h-screen bg-[#FFFDF5] dark:bg-[#1a1816]">
      {/* Dynamic SEO + Open Graph meta tags */}
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonicalUrl} />

        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={article.humorous_summary || article.title} />
        <meta property="og:description" content={article.title} />
        <meta property="og:image" content={shareImage} />
        <meta property="og:site_name" content="Daily Wit" />
        <meta property="article:section" content={article.category} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={canonicalUrl} />
        <meta name="twitter:title" content={article.humorous_summary || article.title} />
        <meta name="twitter:description" content={article.title} />
        <meta name="twitter:image" content={shareImage} />
      </Helmet>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-[#FF6B6B] mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Daily Wit
        </Link>

        {/* Responsive layout: article + desktop sidebar ad */}
        <div className="flex flex-col lg:flex-row gap-8">
          <div className="flex-1 max-w-2xl mx-auto lg:mx-0 w-full">
            <div className="bg-gradient-to-br from-[#FF6B6B] to-[#4ECDC4] border-2 border-black dark:border-white rounded-xl p-1 hard-shadow-lg">
              <div className="bg-white dark:bg-[#2D2A26] rounded-lg overflow-hidden">
                <div
                  className="px-4 py-2 flex items-center justify-between"
                  style={{ backgroundColor: categoryColors[article.category] || '#4ECDC4' }}
                >
                  <span className="font-bold text-sm uppercase tracking-wider text-black">
                    {article.category}
                  </span>
                  <button
                    onClick={handleShare}
                    data-testid="article-share-button"
                    className="p-1.5 rounded-full bg-white/90 hover:bg-white transition-all border border-black/20"
                  >
                    <Share2 className="w-4 h-4 text-black" strokeWidth={2.5} />
                  </button>
                </div>

                <div className="relative">
                  <img
                    src={article.image_url || `https://picsum.photos/800/400?random=${article.id}`}
                    alt={article.title}
                    className="w-full h-72 object-cover"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/95 via-black/80 to-transparent pt-20 pb-6 px-6">
                    <p className="text-white text-2xl font-bold leading-snug" style={{ fontFamily: 'Merriweather, serif' }}>
                      "{article.humorous_summary}"
                    </p>
                  </div>
                </div>

                <div className="p-6">
                  <h1 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">
                    {article.title}
                  </h1>

                  {article.source_url && (
                    <a
                      href={article.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-[#4ECDC4] hover:underline mb-6"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Read full story at {article.source}
                    </a>
                  )}

                  {/* In-article ad slot, nested between content blocks */}
                  <div className="my-6">
                    <AdSlot variant="in-article" />
                  </div>

                  <Reactions newsId={article.id} />
                  <Comments newsId={article.id} />
                  <ShareCard article={article} />
                </div>
              </div>
            </div>

            <div className="text-center mt-8">
              <p className="text-gray-500 dark:text-gray-400 text-sm mb-2">News with a twist from</p>
              <Link
                to="/"
                className="text-2xl font-bold text-[#FF6B6B] hover:text-[#4ECDC4] transition-colors"
                style={{ fontFamily: 'Merriweather, serif' }}
              >
                Daily Wit
              </Link>
              <p className="text-gray-400 text-xs mt-2">Real news. Real laughs. Zero boring stuff.</p>
            </div>
          </div>

          {/* Sidebar ad slot — desktop only */}
          <aside className="hidden lg:block w-[300px] flex-shrink-0">
            <div className="sticky top-24">
              <AdSlot variant="sidebar" />
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default ArticlePage;
